
import React, { createContext, useContext, useState, useCallback } from 'react';
import { ShieldCheck, ShieldAlert, AlertCircle, Info, X } from 'lucide-react';

export type ToastType = 'success' | 'warning' | 'error' | 'info';

interface Toast {
  id: string;
  message: string;
  type: ToastType;
}

interface ToastContextType {
  showToast: (message: string, type: ToastType) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((message: string, type: ToastType) => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts((prev) => [...prev, { id, message, type }]);
    
    // Auto remove after 4 seconds
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const getStyle = (type: ToastType) => {
    switch (type) {
      case 'success': return 'bg-emerald-500/10 border-emerald-500/20 text-emerald-500';
      case 'warning': return 'bg-amber-500/10 border-amber-500/20 text-amber-500';
      case 'error': return 'bg-red-500/10 border-red-500/20 text-red-500';
      case 'info': return 'bg-blue-500/10 border-blue-500/20 text-blue-500';
      default: return 'bg-brand-card border-brand-border text-white';
    }
  };

  const getIcon = (type: ToastType) => {
    switch (type) {
      case 'success': return <ShieldCheck size={18} />;
      case 'warning': return <ShieldAlert size={18} />;
      case 'error': return <AlertCircle size={18} />;
      case 'info': return <Info size={18} />;
      default: return <Info size={18} />;
    }
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      
      {/* Toast Portal/Container */}
      <div className="fixed top-8 right-8 z-[1000] flex flex-col gap-3 pointer-events-none">
        {toasts.map((toast) => (
          <div 
            key={toast.id}
            className={`pointer-events-auto px-6 py-4 rounded-2xl shadow-2xl border flex items-center gap-3 animate-in slide-in-from-right-10 duration-500 min-w-[300px] max-w-md ${getStyle(toast.type)}`}
          >
            <div className="shrink-0">
              {getIcon(toast.type)}
            </div>
            <div className="flex-1">
              <span className="text-[11px] font-black uppercase tracking-widest block leading-tight">
                {toast.message}
              </span>
            </div>
            <button 
              onClick={() => removeToast(toast.id)}
              className="p-1 hover:bg-white/10 rounded-lg transition-colors shrink-0"
            >
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
